import { getCollection, subscribeToCollection } from './firestoreService';
import { AnalyticsEvent, UserEngagementInsights } from '../types/analytics';
import { convertTimestamp } from './firestoreService';
import { Timestamp } from 'firebase/firestore';
import { subDays } from 'date-fns';

interface FirestoreAnalyticsEvent {
  user_id: string;
  event_name: string;
  category: string;
  timestamp: string | Timestamp;
  properties: Record<string, any>;
}

const mapFirestoreEvent = (event: any): AnalyticsEvent => ({
  id: (event as any).id || '',
  userId: event.user_id || '',
  eventName: event.event_name || '',
  category: event.category || '',
  timestamp: convertTimestamp(event.timestamp),
  properties: event.properties || {},
});

/** Fetch from both enhanced_analytics_events and analytics_events, merge and sort by timestamp desc. */
export const getAnalyticsEvents = async (
  days: number = 30,
  category?: string,
  eventName?: string
): Promise<AnalyticsEvent[]> => {
  try {
    const startDate = subDays(new Date(), days);
    const filters: Array<{ field: string; operator: any; value: any }> = [
      {
        field: 'timestamp',
        operator: '>=',
        value: startDate.toISOString(),
      },
    ];

    if (category) {
      filters.push({
        field: 'category',
        operator: '==',
        value: category,
      });
    }

    if (eventName) {
      filters.push({
        field: 'event_name',
        operator: '==',
        value: eventName,
      });
    }

    const limitPerCollection = 5000;
    const [enhanced, standard] = await Promise.all([
      getCollection<FirestoreAnalyticsEvent>(
        'enhanced_analytics_events',
        filters,
        'timestamp',
        'desc',
        limitPerCollection
      ),
      getCollection<FirestoreAnalyticsEvent>(
        'analytics_events',
        filters,
        'timestamp',
        'desc',
        limitPerCollection
      ).catch(() => [] as FirestoreAnalyticsEvent[]),
    ]);

    const combined = [
      ...enhanced.map((e: any) => ({ ...e, id: `enh_${(e as any).id}` })),
      ...standard.map((e: any) => ({ ...e, id: `std_${(e as any).id}` })),
    ];
    combined.sort((a, b) => {
      const ta = convertTimestamp(a.timestamp).getTime();
      const tb = convertTimestamp(b.timestamp).getTime();
      return tb - ta;
    });

    return combined.slice(0, 10000).map((event: any) => mapFirestoreEvent(event));
  } catch (error) {
    console.error('Error fetching analytics events:', error);
    throw error;
  }
};

export const getUserEngagementInsights = async (): Promise<UserEngagementInsights> => {
  try {
    // Get assistant topic added events
    const topicEvents = await getAnalyticsEvents(30, 'user_preferences', 'assistant_topic_added');
    
    // Count topics
    const topicCounts: Map<string, { count: number; category: string }> = new Map();
    for (const event of topicEvents) {
      const title = event.properties.topic_title as string;
      const category = event.properties.category as string || 'Other';
      
      if (title) {
        const existing = topicCounts.get(title) || { count: 0, category };
        topicCounts.set(title, {
          count: existing.count + 1,
          category,
        });
      }
    }

    // Get personalized category events
    const categoryEvents = await getAnalyticsEvents(30, 'user_preferences', 'personalized_category_used');
    const categoryCounts: Map<string, number> = new Map();
    for (const event of categoryEvents) {
      const categoryName = event.properties.category_name as string;
      if (categoryName) {
        categoryCounts.set(categoryName, (categoryCounts.get(categoryName) || 0) + 1);
      }
    }

    // Get learning progress events
    const progressEvents = await getAnalyticsEvents(30, 'learning_engagement', 'learning_progress_updated');
    const topicProgress: Map<string, { total: number; sum: number; completions: number }> = new Map();
    
    for (const event of progressEvents) {
      const topicTitle = event.properties.topic_title as string;
      const completionPercentage = (event.properties.completion_percentage as number) || 0;
      const isCompleted = event.properties.is_completed as boolean || false;
      
      if (topicTitle) {
        const existing = topicProgress.get(topicTitle) || { total: 0, sum: 0, completions: 0 };
        topicProgress.set(topicTitle, {
          total: existing.total + 1,
          sum: existing.sum + completionPercentage,
          completions: existing.completions + (isCompleted ? 1 : 0),
        });
      }
    }

    // Get search query events
    const searchEvents = await getAnalyticsEvents(30, 'user_preferences', 'assistant_search_query');
    const wordCounts: Map<string, number> = new Map();
    const stopWords = new Set([
      'the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for',
      'of', 'with', 'by', 'from', 'as', 'is', 'was', 'are', 'were', 'been',
      'be', 'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would',
      'should', 'could', 'may', 'might', 'must', 'can', 'this', 'that',
      'these', 'those', 'i', 'you', 'he', 'she', 'it', 'we', 'they',
      'what', 'which', 'who', 'whom', 'whose', 'where', 'when', 'why',
      'how', 'all', 'each', 'every', 'both', 'few', 'more', 'most',
      'other', 'some', 'such', 'no', 'nor', 'not', 'only', 'own', 'same',
      'so', 'than', 'too', 'very', 'just', 'about', 'into', 'through',
    ]);

    for (const event of searchEvents) {
      const query = (event.properties.query as string || '').toLowerCase();
      const words = query
        .split(/\W+/)
        .filter((w) => w.length > 2 && !stopWords.has(w));
      
      for (const word of words) {
        wordCounts.set(word, (wordCounts.get(word) || 0) + 1);
      }
    }

    // Sort and format results
    const popularTopics = Array.from(topicCounts.entries())
      .map(([title, data]) => ({
        title,
        count: data.count,
        category: data.category,
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 20);

    const popularCategories = Array.from(categoryCounts.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 20);

    const topProgressTopics = Array.from(topicProgress.entries())
      .map(([title, data]) => ({
        title,
        averageProgress: data.total > 0 ? data.sum / data.total : 0,
        completionCount: data.completions,
      }))
      .sort((a, b) => b.averageProgress - a.averageProgress)
      .slice(0, 20);

    const searchWordCounts = Array.from(wordCounts.entries())
      .map(([word, count]) => ({ word, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 50);

    return {
      popularTopics,
      popularCategories,
      personalizedCategories: popularCategories, // Same data for now
      topProgressTopics,
      searchWordCounts,
      totalSearches: searchEvents.length,
      totalTopicsAdded: topicEvents.length,
    };
  } catch (error) {
    console.error('Error getting user engagement insights:', error);
    throw error;
  }
};

export const subscribeToAnalyticsEvents = (
  callback: (events: AnalyticsEvent[]) => void,
  days: number = 30
): (() => void) => {
  const startDate = subDays(new Date(), days);
  
  return subscribeToCollection<FirestoreAnalyticsEvent>(
    'enhanced_analytics_events',
    (data) => {
      const events = data
        .filter((event) => {
          const timestamp = convertTimestamp(event.timestamp);
          return timestamp >= startDate;
        })
        .map((event: any) => ({
          id: (event as any).id || '',
          userId: event.user_id || '',
          eventName: event.event_name || '',
          category: event.category || '',
          timestamp: convertTimestamp(event.timestamp),
          properties: event.properties || {},
        }));
      callback(events);
    },
    [
      {
        field: 'timestamp',
        operator: '>=',
        value: startDate.toISOString(),
      },
    ],
    'timestamp',
    'desc',
    1000
  );
};

export interface PromptAnalytics {
  ageBandDistribution: Record<string, number>;
  blendingPreferenceUsage: Record<string, number>;
  categoryGenerationMetrics: {
    totalGenerations: number;
    averageGenerationTimeMs: number;
    successRate: number;
  };
  topicGenerationMetrics: {
    totalGenerations: number;
    byCategory: Record<string, number>;
    loadMoreUsage: {
      load_more: number;
      initial: number;
    };
  };
  periodDays: number;
  generatedAt: string;
}

export const getPromptAnalytics = async (days: number = 30): Promise<PromptAnalytics> => {
  try {
    const startDate = subDays(new Date(), days);
    
    // Get personalized category generation events from analytics_events collection
    const categoryEvents = await getCollection<FirestoreAnalyticsEvent>(
      'analytics_events',
      [
        {
          field: 'event_name',
          operator: '==',
          value: 'personalized_categories_generated',
        },
        {
          field: 'timestamp',
          operator: '>=',
          value: startDate.toISOString(),
        },
      ],
      'timestamp',
      'desc',
      10000
    );
    
    // Get topic generation events from analytics_events collection
    const topicEvents = await getCollection<FirestoreAnalyticsEvent>(
      'analytics_events',
      [
        {
          field: 'event_name',
          operator: '==',
          value: 'topics_generated',
        },
        {
          field: 'timestamp',
          operator: '>=',
          value: startDate.toISOString(),
        },
      ],
      'timestamp',
      'desc',
      10000
    );
    
    // Analyze age band distribution
    const ageBandCounts: Record<string, number> = {};
    const blendingPreferenceCounts: Record<string, number> = {};
    const categoryGenerationTimes: number[] = [];
    const topicGenerationCounts: Record<string, number> = {};
    const loadMoreUsage = { load_more: 0, initial: 0 };
    let successfulCategoryGenerations = 0;
    
    for (const event of categoryEvents) {
      const properties = event.properties || {};
      const ageBand = (properties.age_band as string) || 'unknown';
      const blendingPreference = (properties.blending_preference as string) || 'separate';
      const generationTime = (properties.generation_time_ms as number) || 0;
      const success = properties.success === 'true' || properties.success === true;
      
      ageBandCounts[ageBand] = (ageBandCounts[ageBand] || 0) + 1;
      blendingPreferenceCounts[blendingPreference] = (blendingPreferenceCounts[blendingPreference] || 0) + 1;
      if (generationTime > 0) {
        categoryGenerationTimes.push(generationTime);
      }
      if (success) {
        successfulCategoryGenerations++;
      }
    }
    
    for (const event of topicEvents) {
      const properties = event.properties || {};
      const categoryName = (properties.category_name as string) || 'unknown';
      const isLoadMore = properties.is_load_more === 'true' || properties.is_load_more === true;
      
      topicGenerationCounts[categoryName] = (topicGenerationCounts[categoryName] || 0) + 1;
      if (isLoadMore) {
        loadMoreUsage.load_more++;
      } else {
        loadMoreUsage.initial++;
      }
    }
    
    // Calculate average generation time
    const avgCategoryGenerationTime = categoryGenerationTimes.length > 0
      ? categoryGenerationTimes.reduce((a, b) => a + b, 0) / categoryGenerationTimes.length
      : 0;
    
    const successRate = categoryEvents.length > 0
      ? (successfulCategoryGenerations / categoryEvents.length) * 100
      : 0;
    
    return {
      ageBandDistribution: ageBandCounts,
      blendingPreferenceUsage: blendingPreferenceCounts,
      categoryGenerationMetrics: {
        totalGenerations: categoryEvents.length,
        averageGenerationTimeMs: avgCategoryGenerationTime,
        successRate,
      },
      topicGenerationMetrics: {
        totalGenerations: topicEvents.length,
        byCategory: topicGenerationCounts,
        loadMoreUsage,
      },
      periodDays: days,
      generatedAt: new Date().toISOString(),
    };
  } catch (error) {
    console.error('Error getting prompt analytics:', error);
    throw error;
  }
};

